import { AGE_RANGES, EMPTY_FILTER, isFilterEmpty, type DemographicsFilter } from '../types/demographics-filter';
import type { SortDirection, SortKey } from '../types/account-stats';

/**
 * Фильтры вкладки «По аккаунтам» в адресе: теги, демография, сортировка и
 * «Динамика» переживают переход в аккаунт и обратно, и ими можно поделиться.
 */
export type GeneralStatsView = {
  tags: string[];
  filter: DemographicsFilter;
  sortKey: SortKey;
  sortDir: SortDirection;
  comparePrev: boolean;
};

const SORT_KEYS: SortKey[] = ['username', 'followers', 'impressions', 'reach', 'interactions', 'er', 'erByReach'];
export const DEFAULT_VIEW: GeneralStatsView = {
  tags: [],
  filter: EMPTY_FILTER,
  sortKey: 'username',
  sortDir: 'asc',
  comparePrev: false,
};

// Списки — повтором параметра (?tags=a&tags=b): в названиях городов бывают запятые.
const list = (params: URLSearchParams, key: string) => params.getAll(key).map((s) => s.trim()).filter(Boolean);

export function parseGeneralView(params: URLSearchParams): GeneralStatsView {
  const sort = params.get('sort') as SortKey | null;
  const gender = params.get('gender');
  return {
    tags: list(params, 'tags'),
    filter: {
      countries: list(params, 'countries'),
      cities: list(params, 'cities'),
      gender: gender === 'M' || gender === 'F' ? gender : null,
      ageRanges: list(params, 'ages').filter((a) => (AGE_RANGES as readonly string[]).includes(a)),
    },
    sortKey: sort && SORT_KEYS.includes(sort) ? sort : DEFAULT_VIEW.sortKey,
    sortDir: params.get('dir') === 'desc' ? 'desc' : 'asc',
    comparePrev: params.get('dynamics') === '1',
  };
}

/** Пишет вид в `target`; значения по умолчанию в адрес не попадают. */
export function writeGeneralView(target: URLSearchParams, view: GeneralStatsView): URLSearchParams {
  const set = (key: string, value: string | null) => (value ? target.set(key, value) : target.delete(key));
  const setList = (key: string, values: string[]) => {
    target.delete(key);
    for (const v of values) target.append(key, v);
  };
  setList('tags', view.tags);
  const f = isFilterEmpty(view.filter) ? EMPTY_FILTER : view.filter;
  setList('countries', f.countries);
  setList('cities', f.cities);
  set('gender', f.gender);
  setList('ages', f.ageRanges);
  const defaultSort = view.sortKey === DEFAULT_VIEW.sortKey && view.sortDir === DEFAULT_VIEW.sortDir;
  set('sort', defaultSort ? null : view.sortKey);
  set('dir', defaultSort ? null : view.sortDir);
  set('dynamics', view.comparePrev ? '1' : null);
  return target;
}
